import { Clock, AlertTriangle, Sparkles } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { getTrialStatus, TRIAL_DAYS } from '../../constants/pricing'

/**
 * TrialBadge — muestra "Día X de 14 · Y días restantes" con código de color
 * según urgencia. Crea presión sin ser agresivo.
 *
 *   variant="header"  — pill compacto para CajaLayout header
 *   variant="inline"  — versión con texto completo para banners
 *   variant="hero"    — barra con progreso para FreshWelcome / pantallas big
 *
 * Si no hay trial activo (sub active/expired/cancelled) o no se puede
 * determinar el inicio, no renderiza nada.
 */
export default function TrialBadge({ variant = 'header', onClick, className = '' }) {
  const { profile } = useAuth()

  // Determinar inicio del trial. Por simplicidad: created_at del profile.
  const startISO = profile?.created_at
  const status   = getTrialStatus(startISO)
  if (!status) return null

  // Si la suscripción no es trial (activa, expirada, cancelada), no mostrar
  if (profile?.subscription_status && profile.subscription_status !== 'trial') return null

  const { dayN, daysRemaining, pctElapsed, isExpired, urgency } = status

  const tone = {
    fresh:    { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', bar: 'bg-emerald-500',  icon: Sparkles },
    normal:   { bg: 'bg-accent-50',   text: 'text-accent-700',  border: 'border-accent-200',  bar: 'bg-accent-500',   icon: Clock },
    soon:     { bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200',   bar: 'bg-amber-500',    icon: Clock },
    critical: { bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-200',     bar: 'bg-red-500',      icon: AlertTriangle },
    expired:  { bg: 'bg-red-100',     text: 'text-red-800',     border: 'border-red-300',     bar: 'bg-red-600',      icon: AlertTriangle },
  }[urgency]

  const Icon = tone.icon
  const remainingLabel = isExpired
    ? 'Prueba terminada'
    : daysRemaining === 1
      ? '1 día restante'
      : `${daysRemaining} días restantes`

  // ── HEADER VARIANT — pill compacto ─────────────────────────────────────
  if (variant === 'header') {
    return (
      <button
        type="button"
        onClick={onClick}
        title={`Día ${dayN} de ${TRIAL_DAYS} · ${remainingLabel}`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors hover:opacity-80 ${tone.bg} ${tone.text} ${tone.border} ${className}`}
      >
        <Icon size={11} />
        <span className="hidden sm:inline">Día {dayN}/{TRIAL_DAYS}</span>
        <span className="sm:hidden">{daysRemaining}d</span>
      </button>
    )
  }

  // ── INLINE VARIANT — para banners de paywall ────────────────────────────
  if (variant === 'inline') {
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${tone.text} ${className}`}>
        <Icon size={12} />
        Día {dayN} de {TRIAL_DAYS} · {remainingLabel}
      </span>
    )
  }

  // ── HERO VARIANT — barra completa con progreso ──────────────────────────
  return (
    <div className={`rounded-xl border ${tone.border} ${tone.bg} px-4 py-3 ${className}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className={`flex items-center gap-2 text-sm font-semibold ${tone.text}`}>
          <Icon size={14} />
          {isExpired ? 'Tu prueba gratuita terminó' : `Tu prueba gratuita · Día ${dayN} de ${TRIAL_DAYS}`}
        </div>
        <span className={`text-xs font-bold ${tone.text} tabular-nums`}>
          {remainingLabel}
        </span>
      </div>
      <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
        <div
          className={`h-full ${tone.bar} transition-all duration-500`}
          style={{ width: `${pctElapsed}%` }}
        />
      </div>
    </div>
  )
}
